import { Box, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, Spinner, Text, useBreakpointValue, VStack } from "@chakra-ui/react";
import { ChangeEvent, DragEvent, useRef, useState } from "react";

import { G1Button } from "@components/G1Button";
import { $g1Color } from "@/theme/G1Style";
import { G1Icon } from "@utils/icons";

interface ImportContactsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onParseSelectedFile: (file: File) => Promise<void>;
}

const ACCEPTED_EXTENSIONS = [".csv"];

export const SelectImportFileModal = (props: ImportContactsModalProps) => {
    const { isOpen, onClose, onParseSelectedFile } = props;
    const [isDragActive, setIsDragActive] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
 
    const showDropzone = useBreakpointValue({ base: false, md: true }, "md");
 
    const handleFiles = async (files: FileList | null) => {
        if (!files || files.length === 0) { return; }
        try {
            setIsLoading(true);
            await onParseSelectedFile(files[0]);
        } finally {
            setIsLoading(false);
        }
    };
 
    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(false);
        handleFiles(event.dataTransfer.files);
    };
 
    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(true);
    };
 
    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragActive(false);
    };
 
    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        handleFiles(event.target.files);
        event.target.value = "";
    };
 
    const openFileBrowser = () => inputRef.current?.click();
 
    return (
        <Modal isOpen={isOpen} closeOnEsc={true} onClose={onClose} size="sm" isCentered>
            <ModalContent borderRadius="xl" mx={4}>
                <ModalCloseButton top={5} right={5} zIndex={20} />
 
                <ModalBody py={5} px={{ base: 5, md: 8 }} position="relative">
                    {isLoading && (
                        <Flex
                            position="absolute"
                            top={0} left={0} right={0} bottom={0}
                            bg="whiteAlpha.800"
                            borderRadius="xl"
                            align="center"
                            justify="center"
                            zIndex={10}
                        >
                            <Spinner size="lg" color={$g1Color('list.contacts.bg')} thickness="3px" />
                        </Flex>
                    )}

                    <VStack align="stretch" spacing={2} mb={6}>
                        <Text fontSize="xl" fontWeight="bold">Importer des contacts</Text>
                        <Text fontSize="sm" color="gray.500">
                            Fichiers acceptés: CSV
                        </Text>
                    </VStack>
 
                    <input
                        ref={inputRef}
                        type="file"
                        accept={ACCEPTED_EXTENSIONS.join(",")}
                        onChange={handleInputChange}
                        hidden
                    />
 
                    {showDropzone ? (
                        <Box
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onClick={openFileBrowser}
                            cursor="pointer"
                            border="2px dashed"
                            borderColor={isDragActive ? $g1Color('list.contacts.bg') : $g1Color('global.write')}
                            bg={isDragActive ? "gray.100" : "transparent"}
                            borderRadius="lg"
                            pt={5} pb={1}
                            transition="all 0.15s ease"
                        >
                            <VStack spacing={1}>
                                <Box color='white' fontSize="lg" backgroundColor={$g1Color('list.contacts.bg')} borderRadius='full' width={10} height={10} display='flex' alignItems='center' justifyContent="center">
                                    <G1Icon.Upload />
                                </Box>
                                <Text fontWeight="medium" fontSize="md">Glissez-déposez votre fichier ici</Text>
                                <Text fontSize="sm" color="gray.400">ou</Text>
                                <G1Button
                                    component="form"
                                    variant="ok"
                                    title="Parcourir mes fichiers"
                                    fontSize="md"
                                    onClick={(e) => { e.stopPropagation(); openFileBrowser(); }}
                                />
                            </VStack>
                        </Box>
                    ) : (
                        <VStack spacing={4} py={4}>
                            <G1Button
                                component="form"
                                variant="ok"
                                title="Parcourir mes fichiers"
                                width="100%"
                                fontSize="md"
                                onClick={openFileBrowser}
                            />
                        </VStack>
                    )}
 
                    <Box bg={$g1Color('modal.condition.bg')} border="1px solid" borderColor={$g1Color("modal.condition.border")} borderRadius="md" p={3} fontSize="xs" color={$g1Color("modal.condition.write")} mt={6}>
                        <Text as="span" fontWeight="semibold">Nom</Text> et <Text as="span" fontWeight="semibold">Email</Text> obligatoires<br/>
                        <Text as="span" fontWeight="semibold">Prénom</Text> et <Text as="span" fontWeight="semibold">Téléphone</Text> optionnels<br/>
                        <Text as="span">Téléphone au format international mobile (ex : +33 6 12 34 56 78)</Text>
                    </Box>
                    <Text fontSize="xs" color="gray.500" mt={3} textAlign="center">
                        Encodage détecté automatiquement (UTF-8, ISO-8859-1, Windows-1252, MacRoman)
                    </Text>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};